<template>
  <div class="space-y-6 lg:space-y-6">
    <div>
      <div class="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
        <div class="flex-1">
          <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
            {{ $t('BUSINESS_DATA.FILE_SOURCES.TITLE') }}
          </h2>
          <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {{ $t('BUSINESS_DATA.FILE_SOURCES.DESCRIPTION_RAG') }}
          </p>
        </div>

        <woot-button
          class="!bg-grass-500 !text-white w-full xl:w-auto"
          size="small"
          @click="$emit('add-file-source')"
        >
          <fluent-icon icon="add" size="16" class="mr-2" />
          {{ $t('BUSINESS_DATA.FILE_SOURCES.ADD_NEW') }}
        </woot-button>
      </div>

      <p class="mt-2 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
        {{ $t('BUSINESS_DATA.FILE_SOURCES.IMPORT_HINT_RAG') }}
      </p>
    </div>

    <div class="flex flex-col xl:flex-row gap-4">
      <div class="flex-1 relative">
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="`🔍 ${$t('BUSINESS_DATA.FILE_SOURCES.SEARCH_PLACEHOLDER')}`"
            class="w-full pl-4 pr-10 py-3 text-sm border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-hichat-500/20 focus:border-hichat-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md placeholder:text-slate-400"
            @input="onSearch"
          />
          <div v-if="isSearching" class="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div class="w-4 h-4 border-2 border-slate-200 dark:border-slate-600 border-t-hichat-500 rounded-full animate-spin"></div>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-3 xl:flex-row xl:flex-wrap">
        <div class="relative w-full xl:w-auto xl:flex-1">
          <fluent-icon icon="circle" size="16" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none z-10" />
          <select
            v-model="selectedStatus"
            class="pl-10 pr-10 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-hichat-500/20 focus:border-hichat-500 w-full xl:w-48 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
            style="-webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: none;"
            @change="onFilterChange"
          >
            <option value="">
              {{ $t('BUSINESS_DATA.FILE_SOURCES.ALL_STATUS') }}
            </option>
            <option value="pending">{{ $t('BUSINESS_DATA.FILE_SOURCES.STATUS.PENDING') }}</option>
            <option value="processing">{{ $t('BUSINESS_DATA.FILE_SOURCES.STATUS.PROCESSING') }}</option>
            <option value="ready">{{ $t('BUSINESS_DATA.FILE_SOURCES.STATUS.READY') }}</option>
            <option value="error">{{ $t('BUSINESS_DATA.FILE_SOURCES.STATUS.ERROR') }}</option>
          </select>
          <fluent-icon icon="chevron-down" size="14" class="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none z-10" />
        </div>
      </div>
    </div>

    <div>
      <div v-if="isLoading" class="py-8 flex items-center justify-center">
        <Spinner size="md" color-scheme="primary" />
      </div>

      <div v-else-if="fileSources.length === 0" class="text-center py-12">
        <div class="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-2xl p-8 border border-white/30 dark:border-slate-600/30 max-w-md mx-auto">
          <fluent-icon icon="documents" size="48" class="text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            {{ $t('BUSINESS_DATA.FILE_SOURCES.EMPTY_TITLE') }}
          </h3>
          <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">
            {{ $t('BUSINESS_DATA.FILE_SOURCES.EMPTY_DESCRIPTION') }}
          </p>
          <woot-button @click="$emit('add-file-source')" variant="smooth" class="input-base">
            {{ $t('BUSINESS_DATA.FILE_SOURCES.ADD_NEW') }}
          </woot-button>
        </div>
      </div>

      <div v-else class="space-y-4">
        <div
          v-for="src in fileSources"
          :key="src.id"
          class="group bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-md p-2 border border-n-gray-1 dark:border-slate-600/30 hover:bg-white/80 dark:hover:bg-slate-800/80 hover:border-blue-200/50 dark:hover:border-blue-500/30 hover:shadow-lg transition-all duration-300"
        >
          <div class="flex items-start gap-2 sm:gap-3">
            <div class="flex-1 min-w-0">
              <div class="flex flex-wrap items-center gap-2 mb-2">
                <h3 class="text-base font-semibold text-slate-900 dark:text-slate-100 break-words">
                  {{ src.name }}
                </h3>
                <span :class="statusClasses(src.status)" class="flex-shrink-0 px-2 py-0.5 rounded-lg text-xs font-medium">
                  {{ src.status_display }}
                </span>
              </div>
              <p class="text-sm text-slate-600 dark:text-slate-300 break-words">
                {{ src.original_filename }}
              </p>
              <div class="flex flex-wrap gap-3 text-xs text-slate-500 dark:text-slate-400 mt-2">
                <span>{{ $t('BUSINESS_DATA.FILE_SOURCES.UPLOADED_AT') }}: {{ formatDate(src.created_at) }}</span>
                <span v-if="src.processed_at">{{ $t('BUSINESS_DATA.FILE_SOURCES.PROCESSED_AT') }}: {{ formatDate(src.processed_at) }}</span>
                <span>{{ $t('BUSINESS_DATA.FILE_SOURCES.CHUNKS_INGESTED', { count: src.items_count || 0 }) }}</span>
              </div>
              <div v-if="src.error_message" class="mt-2 p-2 rounded bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs">
                {{ src.error_message }}
              </div>
              <div class="mt-3 flex items-center justify-end gap-1 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity">
                <woot-button
                  variant="smooth"
                  size="small"
                  icon="eye-show"
                  color-scheme="secondary"
                  v-tooltip.top="$t('GENERAL.TOOLTIPS.VIEW')"
                  @click="$emit('view-file-source', src)"
                />
                <woot-button
                  variant="smooth"
                  size="small"
                  icon="dismiss-circle"
                  color-scheme="alert"
                  v-tooltip.top="$t('GENERAL.TOOLTIPS.DELETE')"
                  @click="$emit('delete-file-source', src)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <TableFooter
      v-if="!isLoading && fileSources.length > 0 && pagination.total_pages > 1"
      :current-page="pagination.current_page"
      :total-count="pagination.total_count"
      :page-size="pagination.per_page"
      class="border-slate-50 dark:border-slate-800 border-t !px-0 py-4"
      @page-change="goToPage"
    />
  </div>
</template>

<script>
import Spinner from 'shared/components/Spinner.vue';
import { formatDateTimeLocalized } from 'shared/helpers/DateHelper';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
export default {
  name: 'FileSourcesTab',
  components: { Spinner, TableFooter },
  props: {
      fileSources: { type: Array, default: () => [] },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false
      })
    },
    isLoading: { type: Boolean, default: false },
    isSearching: { type: Boolean, default: false }
  },
  emits: ['add-file-source', 'delete-file-source', 'search', 'filter-change', 'page-change', 'view-file-source'],
  data() {
    return {
      searchQuery: '',
      selectedStatus: '',
      searchTimeout: null
    }
  },
  computed: {
  },
  beforeUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = null
    }
  },
  methods: {
    statusClasses(status) {
      const map = {
        pending: 'bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-200',
        processing: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
        ready: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-300',
        error: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300'
      }
      return map[status] || 'bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-200'
    },
    onSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        this.$emit('search', this.searchQuery)
      }, 500)
    },
    onFilterChange() {
      this.$emit('filter-change', { status: this.selectedStatus })
    },
    goToPage(page) {
      if (page >= 1 && page <= this.pagination.total_pages && page !== this.pagination.current_page) {
        this.$emit('page-change', page)
      }
    },
    formatDate(value) {
      if (!value) return ''
      return formatDateTimeLocalized(value, {
        includeSeconds: false,
        localeOverride: this.$i18n?.locale,
      })
    }
  }
}
</script>
